import { useMutation, useQuery } from "@apollo/client";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { ExtensionsPaths } from "@dashboard/extensions/urls";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { Box, Button, Checkbox, Divider, Input, Select, Text, Toggle } from "@saleor/macaw-ui-next";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import {
  browserTimeZone,
  DEFAULT_WINDOW,
  formatIsoDate,
  formatTimeOfDay,
  fromTimeValue,
  FULFILLMENT_METADATA_KEY,
  FulfillmentWindowPayload,
  isIsoDate,
  isResolvableTimeZone,
  nowInZone,
  parseStoredWindow,
  timeZoneOptions,
  toTimeValue,
  WEEKDAYS,
  zoneLabel,
} from "../fulfillmentWindow";
import { UPDATE_FULFILLMENT_SHOP_METADATA } from "../mutations";
import { FULFILLMENT_SHOP_METADATA } from "../queries";

interface ShopMetadataItem {
  key: string;
  value: string;
}

interface ShopMetadataData {
  shop: { id: string; metadata: ShopMetadataItem[] } | null;
}

/**
 * Settings page for the shipping calendar the checkout's delivery estimate is
 * built on: the daily cut-off, the days the warehouse is closed, and the
 * holidays on top of that.
 *
 * Everything is validated HERE rather than left to the storefront. The
 * storefront's rule is to drop a field it cannot trust and fall back, which is
 * the right behaviour for a live checkout but silent — a merchant who typos a
 * zone would simply stop getting a cut-off with nothing to tell them why. The
 * form is the only place that can say so.
 */
const FulfillmentWindowView: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();

  const { data, loading } = useQuery<ShopMetadataData>(FULFILLMENT_SHOP_METADATA, {
    fetchPolicy: "network-only",
  });
  const [updateMetadata] = useMutation(UPDATE_FULFILLMENT_SHOP_METADATA);

  const [form, setForm] = useState<FulfillmentWindowPayload>(DEFAULT_WINDOW);
  const [saved, setSaved] = useState<FulfillmentWindowPayload>(DEFAULT_WINDOW);
  const [newHoliday, setNewHoliday] = useState("");
  const [state, setState] = useState<ConfirmButtonTransitionState>("default");
  // Re-render once a minute so the "it is X right now" line under the picker
  // stays true while the page sits open. That line is the only way a merchant
  // can check they picked their own clock, so a stale one is worse than none.
  const [, setTick] = useState(0);
  // A time input can be mid-edit or empty; that must block save rather than
  // silently persisting whatever the last complete value happened to be.
  const [timeValid, setTimeValid] = useState(true);

  useEffect(() => {
    const timer = window.setInterval(() => setTick(n => n + 1), 30_000);

    return () => window.clearInterval(timer);
  }, []);

  const shopId = data?.shop?.id;

  useEffect(() => {
    if (!data?.shop) return;

    const stored = parseStoredWindow(
      data.shop.metadata.find(entry => entry.key === FULFILLMENT_METADATA_KEY)?.value,
    );
    // Never overwrite a stored zone; only fill an empty one from the browser.
    // The picker shows the live local time beside it, so this guess is visible
    // and checkable rather than silently applied.
    const seeded = stored.time_zone
      ? stored
      : { ...stored, time_zone: browserTimeZone() || DEFAULT_WINDOW.time_zone };

    setForm(seeded);
    // `saved` is the stored value, so seeding a blank zone shows as a pending
    // change the merchant can save or discard, rather than a silent edit.
    setSaved(stored);
  }, [data]);

  const patch = (next: Partial<FulfillmentWindowPayload>) => {
    setState("default");
    setForm(current => ({ ...current, ...next }));
  };

  const shipsOn = (day: number) => !form.non_shipping_days.includes(day);

  const toggleDay = (day: number) => {
    const closed = new Set(form.non_shipping_days);

    if (closed.has(day)) {
      closed.delete(day);
    } else {
      closed.add(day);
    }

    // Closing every day would mean nothing ever ships; the storefront ignores
    // such an entry outright, so refuse it here where it can be explained.
    if (closed.size === WEEKDAYS.length) {
      notify({ status: "error", text: "At least one day has to be a shipping day." });

      return;
    }

    patch({ non_shipping_days: [...closed].sort() });
  };

  const addHoliday = () => {
    if (!isIsoDate(newHoliday)) return;

    if (form.holidays.includes(newHoliday)) {
      notify({ status: "info", text: "That date is already on the list." });
      setNewHoliday("");

      return;
    }

    patch({ holidays: [...form.holidays, newHoliday].sort() });
    setNewHoliday("");
  };

  const removeHoliday = (iso: string) =>
    patch({ holidays: form.holidays.filter(entry => entry !== iso) });

  const zoneError = form.is_active && !isResolvableTimeZone(form.time_zone);
  const zoneOptions = useMemo(
    () => timeZoneOptions(form.time_zone),
    // Labels carry the current abbreviation (EDT vs EST), so they are rebuilt
    // when the selection changes rather than memoised for the session.
    [form.time_zone],
  );
  const localTime = nowInZone(form.time_zone);
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  // Holidays already in the past cannot affect any future estimate. Not an
  // error — a merchant reviewing last year's list should still see it — but
  // worth saying, because a list that only holds stale dates looks configured
  // while doing nothing.
  const staleHolidays = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);

    return form.holidays.filter(iso => iso < today);
  }, [form.holidays]);

  const handleSave = async () => {
    if (!shopId || zoneError || !timeValid) return;

    setState("loading");

    try {
      const result = await updateMetadata({
        variables: {
          id: shopId,
          input: [{ key: FULFILLMENT_METADATA_KEY, value: JSON.stringify(form) }],
        },
      });
      const errors = result.data?.updateMetadata?.errors ?? [];

      if (errors.length) throw new Error(errors[0].message ?? "Metadata update failed");

      setSaved(form);
      setState("success");
      notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
    } catch {
      setState("error");
      notify({ status: "error", text: intl.formatMessage(commonMessages.somethingWentWrong) });
    }
  };

  return (
    <DetailPageLayout gridTemplateColumns={1}>
      <TopNav href={ExtensionsPaths.installedExtensions} title="Fulfillment Window" />
      <DetailPageLayout.Content>
        <Box paddingX={8} paddingY={6} display="flex" flexDirection="column" gap={6}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Text size={6} fontWeight="medium">
              Fulfillment window
            </Text>
            <Text size={3} color="default2">
              Carriers quote a transit time counted from the moment a parcel is picked up, not from
              the moment an order is placed. These settings tell the storefront when parcels
              actually leave, so the delivery dates shown at checkout land on days you are open.
            </Text>
          </Box>

          {loading ? (
            <Text color="default2">Loading…</Text>
          ) : (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={4}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text fontWeight="medium">Apply this window to delivery estimates</Text>
                  <Text size={2} color="default2">
                    Off, checkout counts a carrier&apos;s transit days from the moment the shopper
                    orders — including evenings and weekends.
                  </Text>
                </Box>
                <Toggle
                  name="isActive"
                  pressed={form.is_active}
                  onPressedChange={pressed => patch({ is_active: pressed })}
                />
              </Box>

              <Divider />

              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text fontWeight="medium">Daily cut-off</Text>
                  <Text size={2} color="default2">
                    Orders placed before this time ship the same day. Orders after it ship the
                    next shipping day.
                  </Text>
                </Box>
                {/* Both controls carry Macaw's own floating label, so their field
                    boxes start at the same y. Labelling the Input with `label`
                    and the Select with a Text above it put the two boxes on
                    different baselines, which is what made the row look
                    broken. */}
                <Box display="flex" gap={4} alignItems="flex-start" flexWrap="wrap">
                  <Box display="flex" flexDirection="column" gap={1} __width="180px">
                    <Input
                      type="time"
                      label="Cut-off time"
                      value={toTimeValue(form.cutoff_hour, form.cutoff_minute)}
                      error={!timeValid}
                      onChange={event => {
                        const parsed = fromTimeValue(event.target.value);

                        if (parsed) {
                          setTimeValid(true);
                          patch({ cutoff_hour: parsed.hour, cutoff_minute: parsed.minute });
                        } else {
                          // Mid-edit or cleared. Flag it and leave the last good
                          // value in place rather than writing a partial time.
                          setTimeValid(false);
                          setState("default");
                        }
                      }}
                    />
                    {!timeValid && (
                      <Text size={2} color="critical1">
                        Enter a time of day.
                      </Text>
                    )}
                  </Box>

                  <Box display="flex" flexDirection="column" gap={1} __width="320px">
                    <Select
                      label="Warehouse time zone"
                      options={zoneOptions}
                      value={form.time_zone}
                      size="medium"
                      onChange={value => patch({ time_zone: value as string })}
                    />
                    <Text size={2} color={zoneError ? "critical1" : "default2"}>
                      {zoneError
                        ? "Pick a time zone — the cut-off cannot be applied without one."
                        : localTime
                          ? `It is ${localTime} there right now.`
                          : "The clock the cut-off is read against."}
                    </Text>
                  </Box>
                </Box>

                {/* The rule in words. Two controls cannot say "after 11:00 AM in
                    Toronto" on their own, and that sentence is what a merchant
                    is actually trying to set. */}
                {form.is_active && !zoneError && timeValid && (
                  <Box
                    padding={4}
                    borderRadius={3}
                    backgroundColor="default1"
                    borderStyle="solid"
                    borderWidth={1}
                    borderColor="default1"
                  >
                    <Text size={3}>
                      Orders placed after{" "}
                      <Text size={3} fontWeight="bold">
                        {formatTimeOfDay(form.cutoff_hour, form.cutoff_minute)}
                      </Text>{" "}
                      in{" "}
                      <Text size={3} fontWeight="bold">
                        {zoneLabel(form.time_zone)}
                      </Text>{" "}
                      ship the next shipping day. A shopper in another time zone is judged
                      against this clock, not their own.
                    </Text>
                  </Box>
                )}
              </Box>

              <Divider />

              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text fontWeight="medium">Shipping days</Text>
                  <Text size={2} color="default2">
                    Days parcels leave the warehouse. Unchecked days are skipped when counting both
                    the ship date and the transit days after it.
                  </Text>
                </Box>
                <Box display="flex" gap={5} flexWrap="wrap">
                  {WEEKDAYS.map(day => (
                    <Checkbox
                      key={day.value}
                      name={`ships-${day.value}`}
                      checked={shipsOn(day.value)}
                      onCheckedChange={() => toggleDay(day.value)}
                    >
                      <Text size={3}>{day.label}</Text>
                    </Checkbox>
                  ))}
                </Box>
              </Box>

              <Divider />

              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text fontWeight="medium">Holidays</Text>
                  <Text size={2} color="default2">
                    Individual days you are closed on top of the weekly pattern — statutory
                    holidays, shutdowns, stocktakes.
                  </Text>
                </Box>

                <Box display="flex" gap={3} alignItems="flex-end" flexWrap="wrap">
                  <Input
                    type="date"
                    label="Add a date"
                    value={newHoliday}
                    onChange={event => setNewHoliday(event.target.value)}
                  />
                  <Button
                    variant="secondary"
                    disabled={!isIsoDate(newHoliday)}
                    onClick={addHoliday}
                  >
                    Add
                  </Button>
                </Box>

                {form.holidays.length === 0 ? (
                  <Text size={2} color="default2">
                    No holidays yet. Weekends are set above, so add only the extra closures.
                  </Text>
                ) : (
                  <Box display="flex" flexDirection="column" borderStyle="solid" borderWidth={1} borderColor="default1" borderRadius={3}>
                    {form.holidays.map((iso, index) => (
                      <Box
                        key={iso}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        paddingX={4}
                        paddingY={3}
                        borderTopStyle={index === 0 ? "none" : "solid"}
                        borderTopWidth={index === 0 ? 0 : 1}
                        borderColor="default1"
                      >
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Text size={3}>{formatIsoDate(iso)}</Text>
                          <Text size={2} color="default2">
                            {iso}
                            {staleHolidays.includes(iso) ? " — in the past, affects nothing" : ""}
                          </Text>
                        </Box>
                        <Button
                          variant="tertiary"
                          size="small"
                          onClick={() => removeHoliday(iso)}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </DetailPageLayout.Content>

      <Savebar>
        <Savebar.Spacer />
        <Savebar.CancelButton
          onClick={() => {
            setForm(saved);
            setTimeValid(true);
          }}
        />
        <Savebar.ConfirmButton
          transitionState={state}
          onClick={handleSave}
          disabled={!dirty || zoneError || !timeValid || !shopId}
        />
      </Savebar>
    </DetailPageLayout>
  );
};


export default FulfillmentWindowView;
